import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'

// Third party
import clsx from 'clsx'
import { capitalize } from 'lodash'

// Redux
import {
  setExhibitorRefundErrors,
  setExhibitorRefundTotals,
} from '../../../../store/exhibitor/exhibitorSlice'
import { useAppDispatch } from '../../../../store/hooks'

import AmountInput from '../../../../components/common/inputs/AmountInput'
import { createString } from '../../../../helpers/helpers'
import { getFloatPrice, removeLeadingZeros } from '../../../../helpers/price'
import { getRiderHorses } from '../../../../helpers/riders'
import useHorse from '../../manage/common/table/HorsesManage/hooks/useHorse'
import { cn } from '../../../../lib/utils'

import {
  HORSE_NAME_DEFAULT,
  IRegistrationByDayInterface,
} from '../../../../models/registrations-by-day/registrationByDay.interface'
import { IOnRefundAmountChangeFn } from '../ExhibitorProfileDisplayTab'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IHorseData } from '../../../../models/horse/horse.interface'
import { getCurrencyFromAmount } from '../../../../helpers/payment'

type IExhibitorRegistrationRefundAccordionProps = {
  onRefundAmountChange?: IOnRefundAmountChangeFn
  registrationsByDay: IRegistrationByDayInterface[]
  eventData: IEventReviewPublish | null
  currency: string
}

type IErrorsRef = {
  [key: string]: {
    message: string
    toastMessage: string
  }
}

type IValidate = (inputName: string, maxCost: number, value: number, eventDay: string) => void

type IDetailRowProps = {
  errors: React.MutableRefObject<IErrorsRef>
  registrationByDay: IRegistrationByDayInterface
  validate: IValidate
  eventData: IEventReviewPublish | null
  horses: IHorseData[]
  currency: string
}

const ExhibitorRegistrationInput: FC<{
  className?: string
  data: ReactNode
  icon?: string
}> = ({ className, data, icon }) => {
  return (
    <div className={cn('flex flex-col justify-center', className)}>
      {icon ? (
        <img className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2" src={icon} alt="icons" />
      ) : null}
      <div className={clsx('max-w-full', icon && 'pl-6')}> {data}</div>
    </div>
  )
}

const DetailRow = ({ registrationByDay, validate, errors, horses, currency }: IDetailRowProps) => {
  let currAmountFieldName = `${registrationByDay.id}`
  let [currentValue, setCurrentValue] = useState<number | string>(registrationByDay.amountRefunded)

  return (
    <div className="flex flex-col md:flex-row mb-3 md:mb-0 w-full">
      <ExhibitorRegistrationInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/6 overflow-hidden whitespace-nowrap text-ellipsis"
        data={capitalize(createString(registrationByDay.registrationByDayName))}
      />

      <ExhibitorRegistrationInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/6 flex justify-center items-center text-center relative"
        icon="/assets/og_icons/YearofHorse-1.svg"
        data={horses?.map((item) => item.horseName).join(', ') || HORSE_NAME_DEFAULT}
      />

      <ExhibitorRegistrationInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/6 text-center relative"
        icon="/assets/cp_icons/User-1.svg"
        data={capitalize(createString(registrationByDay.riderName))}
      />

      <ExhibitorRegistrationInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/6 text-center"
        data={
          <span>
            {getFloatPrice(`${currency}${registrationByDay.registrationPrice}`)}{' '}
            {registrationByDay.isQualify && (
              <span>
                • {getFloatPrice(`${currency}${registrationByDay.qualifyFee}`)}{' '}
                {registrationByDay.qualifyFeeName || 'qual'}
              </span>
            )}
          </span>
        }
      />

      <ExhibitorRegistrationInput
        className="rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor min-h-[48px] bg-SeabiscuitGrayThemeColor p-3 m-1 w-1/6 text-center"
        data={registrationByDay.isScratched ? 'Scratched' : 'No'}
      />
      <AmountInput
        withDecimals
        name={currAmountFieldName}
        prefix={currency}
        placeholder="Enter amount..."
        disable={registrationByDay?.refundAmountAdded || !registrationByDay.isScratched}
        inputClassName={clsx(
          'w-full bg-transparent outline-0 ring-0 border-0 focus:outline-0 focus:ring-0 focus:border-0 text-SeabiscuitDark200ThemeColor',
          !!errors?.current?.[currAmountFieldName] && 'refundError text-[#EB5757]'
        )}
        className={clsx(
          'text-[14px] border !border-solid rounded-md flex items-center p-4 min-h-[48px] text-SeabiscuitDark200ThemeColor w-1/6 m-1 focus:bg-white',
          !registrationByDay?.refundAmountAdded
            ? !!errors?.current?.[currAmountFieldName] &&
                'text-[#EB5757] !bg-[#EB57571A] border-red-500'
            : '!bg-SeabiscuitGrayThemeColor',
          registrationByDay?.refundAmountAdded || !registrationByDay.isScratched
            ? 'bg-SeabiscuitGrayThemeColor'
            : 'bg-white'
        )}
        title={errors?.current?.[currAmountFieldName]?.message}
        onChange={(value: string) => {
          if (registrationByDay.refundAmountAdded) return

          const newValue = removeLeadingZeros(value.replace(getCurrencyFromAmount(value), ''))
          setCurrentValue(newValue)

          validate(
            currAmountFieldName,
            (registrationByDay.registrationPrice || 0) +
              (registrationByDay?.isQualify ? Number(registrationByDay?.qualifyFee) || 0 : 0),
            Number(newValue),
            registrationByDay.registrationByDayName ?? ''
          )
        }}
        value={currentValue}
      />
    </div>
  )
}

const ExhibitorRegistrationRefundAccordion = ({
  onRefundAmountChange,
  registrationsByDay,
  eventData,
  currency,
}: IExhibitorRegistrationRefundAccordionProps) => {
  // Hooks and vars
  const errors = useRef<IErrorsRef>({})

  const dispatch = useAppDispatch()
  const { allHorses } = useHorse(eventData?.id || '')

  const [refundsTotal, setRefundsTotal] = useState(0)
  const [itemsSelectedForRefund, setItemsSelectedForRefund] = useState(0)

  useEffect(() => {
    let selectedIds: string[] = []
    let total = 0

    registrationsByDay.forEach((registrationByDay) => {
      if (registrationByDay.amountRefunded > 0 && !registrationByDay.refundAmountAdded) {
        total += registrationByDay.amountRefunded
        selectedIds.push(registrationByDay.id ?? '')
      }
    })

    setRefundsTotal(total)
    setItemsSelectedForRefund(selectedIds.length)

    const selectedRegistrationsByDay = registrationsByDay.filter((registrationByDay) =>
      selectedIds.includes(registrationByDay.id ?? '')
    )

    dispatch(
      setExhibitorRefundTotals({
        keyToUpdate: 'eventsRefundPriceTotal',
        value: selectedRegistrationsByDay,
      })
    )
  }, [dispatch, registrationsByDay])

  const validate: IValidate = (inputName, maxCost, value, name) => {
    let message: string | null = null

    if (!maxCost || !inputName) {
      return delete errors.current?.[inputName]
    }

    if (value > maxCost)
      errors.current = {
        ...errors.current,
        [inputName]: {
          message: `max value that can be refunded is ${currency}${maxCost}`,
          toastMessage: `${name} max value that can be refunded is ${currency}${maxCost}`,
        },
      }
    else delete errors.current?.[inputName]

    onRefundAmountChange?.({
      type: 'classes',
      itemDetails: {
        itemId: inputName,
        itemRefundAmount: value,
      },
    })

    if (Object.keys(errors.current).length)
      message = errors.current[Object.keys(errors.current)[0]]?.toastMessage ?? null

    dispatch(
      setExhibitorRefundErrors({
        keyToUpdate: 'eventsError',
        message,
      })
    )
  }

  return (
    <>
      <div className="flex flex-col md:flex-row mb-3 md:mb-0 md:items-center w-full">
        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-1 font-semibold w-1/6')}
          data={`Class`}
        />

        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/6')}
          data={`Registered horse`}
        />

        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/6')}
          data={`Registered rider`}
        />

        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/6')}
          data={`Amount paid`}
        />

        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/6')}
          data={`Scratched?`}
        />

        <ExhibitorRegistrationInput
          className={clsx('text-xs text-SeabiscuitDark200ThemeColor ml-2 font-semibold w-1/6')}
          data={`Enter Refund Amount`}
        />
      </div>
      {registrationsByDay.map((currentItem, index) => (
        <DetailRow
          key={index}
          errors={errors}
          validate={validate}
          registrationByDay={currentItem}
          eventData={eventData}
          horses={getRiderHorses(allHorses, currentItem?.horsesSelected || [])}
          currency={currency}
        />
      ))}

      <div className="flex flex-col md:flex-row mb-3 md:mb-0 md:items-center w-full">
        <ExhibitorRegistrationInput
          className={clsx(
            'rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor text-opacity-[.85] font-normal min-h-[48px] bg-SeabiscuitGreenLightThemeColor bg-opacity-10 p-3 m-1 w-1/6'
          )}
          data="Total"
        />

        <ExhibitorRegistrationInput
          className={clsx(
            'rounded-lg xl:text-[14px] 2xl:text-base text-SeabiscuitDark200ThemeColor text-opacity-[.85] font-normal min-h-[48px] bg-SeabiscuitGreenLightThemeColor bg-opacity-10 p-3 m-1 w-5/6 text-right'
          )}
          data={`${itemsSelectedForRefund} selected item, ${getFloatPrice(`${currency}${refundsTotal}`)} will be refunded`}
        />
      </div>
    </>
  )
}

export default ExhibitorRegistrationRefundAccordion
